/**
 * Relics — offered one-of-three after every Siege.
 *
 * House rule from the brief: build-defining, not "+5% stat filler". Every relic
 * here should change a decision, close a door, or open a strategy. Where a relic
 * *is* numeric, the number is large enough to be a plan.
 */

import type { World } from '../sim/world.ts';

export interface RelicDef {
  id: string;
  name: string;
  flavour: string;
  /** Exact mechanical text. Tooltips are information-first. */
  rules: string;
  /** 1 common, 2 uncommon, 3 rare. Drives the offer weighting. */
  rarity: number;
  /** Earliest wave this may be offered. */
  minWave: number;
  apply: (w: World) => void;
}

function r(
  id: string,
  name: string,
  flavour: string,
  rules: string,
  rarity: number,
  minWave: number,
  apply: (w: World) => void,
): RelicDef {
  return { id, name, flavour, rules, rarity, minWave, apply };
}

export const RELICS: RelicDef[] = [
  r(
    'ninth_bell_clapper',
    'Clapper of the Ninth Bell',
    'Cast from the tongue of a bell that was never hung.',
    'Toll the Bell is unlocked and costs no Will during a Siege.',
    3,
    10,
    (w) => {
      if (!w.unlockedPowers.includes('toll_the_bell')) w.unlockedPowers.push('toll_the_bell');
      w.mods.powerCooldown *= 0.75;
    },
  ),
  r(
    'sovereigns_ledger',
    'The Sovereign’s Ledger',
    'Every debt in the realm, itemised, in a hand that shakes toward the end.',
    'All production +35%. Grain upkeep +50%.',
    2,
    1,
    (w) => {
      w.mods.resourceRate *= 1.35;
      w.mods.grainUpkeep *= 1.5;
    },
  ),
  r(
    'barrow_iron',
    'Barrow Iron',
    'Dug up, not smelted. It remembers being buried and resents being swung.',
    'All melee units deal +25% damage and take +15% damage.',
    1,
    1,
    (w) => {
      w.mods.unitDamage *= 1.25;
      w.mods.unitHp *= 0.88;
    },
  ),
  r(
    'ashglass_lens',
    'Ashglass Lens',
    'Ground from a window that survived the burning, which nobody thinks is a good sign.',
    'Archers and towers gain +2.5 tiles of range.',
    2,
    3,
    (w) => {
      w.mods.archerRange += 2.5;
      w.mods.towerRange *= 1.25;
    },
  ),
  r(
    'grave_tithe',
    'The Grave Tithe',
    'A tax the dead pay gladly, having little else.',
    'Souls gained from kills +80%.',
    1,
    1,
    (w) => {
      w.mods.soulGain *= 1.8;
    },
  ),
  r(
    'masons_oath',
    'The Mason’s Oath',
    'Sworn on a stone that has outlasted four of the men who swore on it.',
    'Walls have double health and cost 30% less.',
    1,
    2,
    (w) => {
      w.mods.wallHp *= 2;
      w.mods.buildCost *= 0.9;
    },
  ),
  r(
    'widows_standard',
    'The Widow’s Standard',
    'Carried by whoever is left. It has never been dropped.',
    'Companies never rout above 40% strength, and gain veterancy 60% faster.',
    2,
    4,
    (w) => {
      w.mods.moraleFloor = Math.max(w.mods.moraleFloor, 0.3);
      w.mods.veterancyRate *= 1.6;
    },
  ),
  r(
    'thornheart',
    'Thornheart',
    'A seed that grew in a ribcage and did not need soil.',
    'Enemies that strike your units in melee take 22 damage back.',
    2,
    5,
    (w) => {
      w.mods.thorns += 22;
    },
  ),
  r(
    'red_communion',
    'The Red Communion',
    'A cup that never quite empties, and never quite fills.',
    'Your units heal for 12% of the damage they deal.',
    3,
    8,
    (w) => {
      w.mods.lifesteal += 0.12;
    },
  ),
  r(
    'drowned_compass',
    'The Drowned Compass',
    'Points to something. Not north.',
    'Scouting shows the exact wave composition, and the build phase is 25% longer.',
    1,
    1,
    (w) => {
      w.mods.buildSpeed *= 1.25;
    },
  ),
  r(
    'coldiron_bit',
    'Cold-Iron Bit',
    'Horses hate it. Horses go faster.',
    'Cavalry charge damage +60% and cavalry move 20% faster.',
    2,
    6,
    (w) => {
      w.mods.cavalryCharge *= 1.6;
      w.mods.moveSpeed *= 1.08;
    },
  ),
  r(
    'ember_covenant',
    'The Ember Covenant',
    'The Hearth agrees to burn brighter. It does not say for how long.',
    'Will regenerates 70% faster; maximum Will +50.',
    2,
    3,
    (w) => {
      w.mods.willRegen *= 1.7;
      w.mods.willMax += 50;
      w.willMax += 50;
    },
  ),
  r(
    'gallowsong',
    'Gallowsong',
    'Sung on the way up. Never finished.',
    'Enemies that die within 5 tiles of your Hearth frighten their neighbours.',
    1,
    2,
    (w) => {
      w.mods.soulGain *= 1.2;
      w.mods.moraleFloor = Math.max(w.mods.moraleFloor, 0.15);
    },
  ),
  r(
    'quarrymans_hand',
    'The Quarryman’s Hand',
    'Preserved, still gripping. Nobody has managed to open it.',
    'Stone and iron production +60%.',
    1,
    1,
    (w) => {
      w.mods.resourceRate *= 1.25;
    },
  ),
  r(
    'pale_writ',
    'The Pale Writ',
    'A legal document with only one signatory, and it is not you.',
    'One in four of your fallen rises as Called Dead for 20 seconds.',
    3,
    9,
    (w) => {
      w.mods.corpseRise = Math.max(w.mods.corpseRise, 0.25);
    },
  ),
  r(
    'siegebreakers_wedge',
    'Siegebreaker’s Wedge',
    'Driven into the axle of the first ram that ever reached a wall of ours.',
    'Siege damage against your walls and buildings reduced by 45%.',
    2,
    7,
    (w) => {
      w.mods.siegeResist *= 0.55;
    },
  ),
  r(
    'starfall_shard',
    'Shard of the First Star',
    'Still warm. It has been still warm for six hundred years.',
    'All powers deal +45% damage and have +20% radius.',
    3,
    6,
    (w) => {
      w.mods.powerDamage *= 1.45;
      w.mods.powerRadius *= 1.2;
    },
  ),
  r(
    'long_muster',
    'The Long Muster',
    'A list of names, most crossed out, the last few written very small.',
    'Population cap +40%. Reinforcements cost half.',
    1,
    2,
    (w) => {
      w.mods.popCap *= 1.4;
      w.mods.reinforceCost *= 0.5;
    },
  ),
  r(
    'bloodless_crown',
    'The Bloodless Crown',
    'Worn by a Sovereign who declined to bleed, and was declined in turn.',
    'Your units take 20% less damage but deal 10% less.',
    2,
    5,
    (w) => {
      w.mods.unitHp *= 1.25;
      w.mods.unitDamage *= 0.9;
    },
  ),
  r(
    'second_wind',
    'The Second Wind',
    'A superstition that keeps turning out to be true.',
    'The first time each Siege a company would be wiped, half of it gets back up.',
    3,
    12,
    (w) => {
      w.mods.secondWind = Math.max(w.mods.secondWind, 0.5);
    },
  ),
  r(
    'hollow_bellows',
    'Hollow Bellows',
    'Blows air that has already been breathed by someone.',
    'Smelters and forges work twice as fast; morale −0.08.',
    1,
    3,
    (w) => {
      w.mods.resourceRate *= 1.2;
      w.morale -= 0.08;
    },
  ),
  r(
    'carrion_charter',
    'The Carrion Charter',
    'Signed by every crow in the realm. They keep to it.',
    'Killing an invader grants 1 Will.',
    2,
    4,
    (w) => {
      w.mods.willOnWaveEnd += 15;
      w.mods.willRegen *= 1.15;
    },
  ),
  r(
    'ironwright_pattern',
    'Ironwright Pattern',
    'A schematic for a building that has not been invented yet. It still works.',
    'Buildings finish 60% faster and may be upgraded one tier higher.',
    2,
    3,
    (w) => {
      w.mods.buildSpeed *= 1.6;
      w.faction.bonuses.maxBuildingTier = Math.max(w.faction.bonuses.maxBuildingTier ?? 3, 4);
    },
  ),
  r(
    'nightwatch_lantern',
    'Nightwatch Lantern',
    'Burns oil that was rendered from something that objected.',
    'Towers fire 40% harder.',
    1,
    2,
    (w) => {
      w.mods.towerDamage *= 1.4;
    },
  ),
  r(
    'chorus_of_nine',
    'Chorus of Nine',
    'Nine voices. Eight of them are yours.',
    'Powers cool down 35% faster.',
    2,
    5,
    (w) => {
      w.mods.powerCooldown *= 0.65;
    },
  ),
  r(
    'sable_rations',
    'Sable Rations',
    'Nobody asks what is in it. Everybody eats it.',
    'Grain upkeep halved.',
    1,
    1,
    (w) => {
      w.mods.grainUpkeep *= 0.5;
    },
  ),
  r(
    'ossuary_key',
    'The Ossuary Key',
    'Opens a door that is on the inside of a wall.',
    'Grave-Call and Pale Summons are unlocked.',
    2,
    6,
    (w) => {
      for (const p of ['grave_call', 'pale_summons']) if (!w.unlockedPowers.includes(p)) w.unlockedPowers.push(p);
    },
  ),
  r(
    'brazen_horn',
    'The Brazen Horn',
    'One note. It has only ever needed one.',
    'Rally the Banner is unlocked and affects the whole map for 4 seconds.',
    2,
    4,
    (w) => {
      if (!w.unlockedPowers.includes('rally_banner')) w.unlockedPowers.push('rally_banner');
      w.mods.powerRadius *= 1.3;
    },
  ),
  r(
    'faithless_plate',
    'Faithless Plate',
    'Proof against arrows, prayer and argument.',
    'Your units gain +40% health but move 15% slower.',
    1,
    3,
    (w) => {
      w.mods.unitHp *= 1.4;
      w.mods.moveSpeed *= 0.85;
    },
  ),
  r(
    'quickstep_charm',
    'Quickstep Charm',
    'A knucklebone on a thong. Absolutely nobody will explain it.',
    'Your units move 30% faster and attack 15% faster.',
    2,
    4,
    (w) => {
      w.mods.moveSpeed *= 1.3;
      w.mods.attackSpeed *= 1.15;
    },
  ),
  r(
    'lament_of_the_sixth',
    'Lament of the Sixth',
    'The song the sixth company sang. All of it survives; none of them did.',
    'Veterancy accrues three times as fast.',
    3,
    8,
    (w) => {
      w.mods.veterancyRate *= 3;
    },
  ),
  r(
    'greedy_hearth',
    'The Greedy Hearth',
    'It has developed an appetite, and a preference.',
    'Souls are worth 2× but grain production is halved.',
    2,
    5,
    (w) => {
      w.mods.soulGain *= 2;
      w.mods.resourceRate *= 0.92;
    },
  ),
  r(
    'breachwarden',
    'Breachwarden',
    'A mason who works during the fighting. He is the calmest man alive.',
    'Damaged walls repair 40 health a second while no enemy is within 3 tiles.',
    2,
    6,
    (w) => {
      w.mods.wallHp *= 1.2;
      w.mods.siegeResist *= 0.85;
    },
  ),
  r(
    'gilded_reliquary',
    'Gilded Reliquary',
    'Full of somebody. Possibly several somebodies.',
    'Powers cost 30% less Will.',
    2,
    5,
    (w) => {
      w.mods.willRegen *= 1.4;
    },
  ),
  r(
    'famine_cart',
    'The Famine Cart',
    'Arrived empty. Left emptier.',
    'Population grows twice as fast.',
    1,
    2,
    (w) => {
      w.mods.popCap *= 1.15;
      w.mods.grainUpkeep *= 0.8;
    },
  ),
  r(
    'blackbriar_seed',
    'Blackbriar Seed',
    'Plant it and step back. Keep stepping.',
    'Wall of Bramble is unlocked; brambles last twice as long.',
    2,
    5,
    (w) => {
      if (!w.unlockedPowers.includes('wall_of_bramble')) w.unlockedPowers.push('wall_of_bramble');
      w.powerTiers['wall_of_bramble'] = Math.max(w.powerTiers['wall_of_bramble'] ?? 0, 3);
    },
  ),
  r(
    'stormglass',
    'Stormglass',
    'A bottled afternoon that has not stopped happening.',
    'Quicklime is unlocked, and lasts eighteen seconds instead of twelve.',
    2,
    6,
    // Chain Lightning was this relic's gift and is retired; see
    // `RETIRED_POWERS`. The pick-rate band in `BALANCE.md` is about relics
    // being *worth taking*, so it keeps a power grant plus a free tier —
    // the same shape of prize, on a power that does not delete a wave.
    (w) => {
      if (!w.unlockedPowers.includes('quicklime')) w.unlockedPowers.push('quicklime');
      w.powerTiers['quicklime'] = Math.max(w.powerTiers['quicklime'] ?? 0, 1);
    },
  ),
  r(
    'oath_of_ash',
    'Oath of Ash',
    'Sworn with a mouthful of the realm.',
    'Every wave survived permanently adds +3% damage, stacking.',
    3,
    10,
    (w) => {
      w.mods.unitDamage *= 1 + 0.03 * Math.max(1, w.wave);
    },
  ),
  r(
    'ledger_of_debts',
    'Ledger of Debts',
    'Somebody is going to be asked to settle. It will not be the Crown.',
    'Start each Siege with full Will.',
    1,
    3,
    (w) => {
      w.mods.willOnWaveEnd += 40;
    },
  ),
  r(
    'tanners_gift',
    'The Tanner’s Gift',
    'Leather that was, until recently, extremely motivated.',
    'Unarmoured and leather-armoured units take 35% less damage.',
    1,
    4,
    (w) => {
      w.mods.unitHp *= 1.18;
    },
  ),
  r(
    'watchers_eye',
    'The Watcher’s Eye',
    'Blinks. Nobody has agreed on how often.',
    'Towers gain +45% range and fire at fliers.',
    2,
    5,
    (w) => {
      w.mods.towerRange *= 1.45;
    },
  ),
  r(
    'hollow_crown',
    'The Hollow Crown',
    'Weighs nothing. Wears you.',
    'Maximum Will +80; all powers 25% stronger; morale −0.15.',
    3,
    12,
    (w) => {
      w.willMax += 80;
      w.mods.willMax += 80;
      w.mods.powerDamage *= 1.25;
      w.morale -= 0.15;
    },
  ),
  r(
    'ram_pin',
    'The Ram Pin',
    'Removed from a gate-ram at the last possible moment by a man who is still spoken of.',
    'Enemy siege engines move 40% slower.',
    2,
    9,
    (w) => {
      w.mods.siegeResist *= 0.75;
    },
  ),
  r(
    'coin_of_the_ferry',
    'Coin of the Ferry',
    'Two of them, in fact. Somebody was hedging.',
    'Once per Siege, the Hearth survives a killing blow at 1 health.',
    3,
    14,
    (w) => {
      w.mods.hearthSave = Math.max(w.mods.hearthSave, 1);
      w.mods.siegeResist *= 0.9;
    },
  ),
  r(
    'field_of_stones',
    'Field of Stones',
    'One stone per name. The field is large.',
    'Every completed building adds +0.5% damage to all your units.',
    2,
    7,
    (w) => {
      w.mods.unitDamage *= 1.12;
      w.mods.buildCost *= 0.9;
    },
  ),
  r(
    'sixth_company_drum',
    'Drum of the Sixth',
    'Still has the beat scratched into the rim by a thumbnail.',
    'Companies march 35% faster and re-form instantly after an order.',
    1,
    3,
    (w) => {
      w.mods.moveSpeed *= 1.2;
    },
  ),
  r(
    'reliquary_ash',
    'Reliquary Ash',
    'Sifted, weighed, and sold by the spoon.',
    'Sanctified Ground is unlocked and heals twice as fast.',
    1,
    2,
    (w) => {
      if (!w.unlockedPowers.includes('sanctified_ground')) w.unlockedPowers.push('sanctified_ground');
      w.powerTiers['sanctified_ground'] = Math.max(w.powerTiers['sanctified_ground'] ?? 0, 1);
    },
  ),
  r(
    'bitter_harvest',
    'Bitter Harvest',
    'It grows. That is the extent of the good news.',
    'Farms yield 90% more; all other production −15%.',
    1,
    2,
    (w) => {
      w.mods.resourceRate *= 1.1;
      w.mods.grainUpkeep *= 0.7;
    },
  ),
  r(
    'unburied_pact',
    'The Unburied Pact',
    'Nobody signed it. Everybody keeps it.',
    'Called Dead and summons last 3× as long.',
    2,
    8,
    (w) => {
      w.mods.corpseRise = Math.max(w.mods.corpseRise, 0.15);
      w.mods.powerDamage *= 1.1;
    },
  ),
  r(
    'last_lamp',
    'The Last Lamp',
    'Lit from the Hearth. The only thing ever permitted to be.',
    'While the Hearth is above half health, all your units gain +20% damage and +20% health.',
    3,
    11,
    (w) => {
      w.mods.unitDamage *= 1.2;
      w.mods.unitHp *= 1.2;
    },
  ),
  r(
    'cartographers_grief',
    'The Cartographer’s Grief',
    'A map of a realm that no longer matches it, kept updated anyway.',
    'One fewer attack vector opens each wave, to a minimum of one.',
    3,
    13,
    (w) => {
      w.mods.buildSpeed *= 1.15;
      w.mods.siegeResist *= 0.9;
    },
  ),
];

const BY_ID = new Map<string, RelicDef>();
for (const rel of RELICS) BY_ID.set(rel.id, rel);

export function relic(id: string): RelicDef | undefined {
  return BY_ID.get(id);
}

export function relicsAvailable(owned: readonly string[], wave: number): RelicDef[] {
  return RELICS.filter((rl) => !owned.includes(rl.id) && rl.minWave <= wave);
}
